import * as React from "react";
import { DragSource } from "react-dnd";
// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
// Controls
import { Checkbox } from "../checkbox/Checkbox";
import { TextField } from "../../textField/TextField";
import { TextFieldType } from "../../textField/TextField";
import { DndListItem } from "../dndList/DndListItem";
import { IconButton } from "../../iconButton/IconButton";
// Styles
import { uploadIconProps, deleteIconProps } from "../../../icons/icons.styles";
import { getClassNames, getStyles } from "./ChecklistItem.styles";
// Utilities
import { css } from "@fluentui/utilities/lib/css";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
//Models
import { ChecklistPermissions, isChecklistPermissionDisabled } from "@ms/uno-models/lib/local/client/permissions/ChecklistPermissions";
import { ChecklistItem as ChecklistItemModel } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ChecklistItem";
export var ChecklistItemFocusState;
(function(ChecklistItemFocusState) {
    // Not focused on this checklist item
    ChecklistItemFocusState[ChecklistItemFocusState["None"] = 0] = "None";
    // Focused on the entire checklist item element
    ChecklistItemFocusState[ChecklistItemFocusState["ListItem"] = 1] = "ListItem";
    // Focused on the checklist item title for editing
    ChecklistItemFocusState[ChecklistItemFocusState["Title"] = 2] = "Title";
    // Focused on one of the promote/delete buttons
    ChecklistItemFocusState[ChecklistItemFocusState["Buttons"] = 3] = "Buttons";
})(ChecklistItemFocusState || (ChecklistItemFocusState = {}));
/** Task for converting ChecklistItem class component to Functional: https://office.visualstudio.com/PathWM/_workitems/edit/8394634 */ /** Standalone Checklist item component. Should be wrapped by a DndList */ class ChecklistItem extends DndListItem {
    /**
     * Name of the component
     * @override
     */ name() {
        return "ChecklistItem";
    }
    /**
     * @inheritdoc
     * @override
     */ getLiProps() {
        const { isDragging, listItem } = this.props;
        const componentClassNames = this.getClassNames();
        const listItemClassName = css(componentClassNames.root, isDragging ? "dragging" : "");
        return {
            className: listItemClassName,
            key: listItem.id
        };
    }
    /**
     * @inheritdoc
     * @override
     */ getSubLiDivProps() {
        const { itemTypeString, listItem, index, permissions } = this.props;
        const disableReorder = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableReorder);
        return {
            ["aria-labelledby"]: this.titleElementId,
            className: ChecklistItem.SUB_LI_DIV_CLASSNAME,
            role: "checkbox",
            tabIndex: 0,
            ref: this.checklistItemElement,
            onKeyDown: this.preventDefault,
            onKeyUpCapture: this.onKeyUp,
            ["data-dnd-role"]: disableReorder ? undefined : itemTypeString,
            ["aria-checked"]: listItem.isChecked,
            ["data-index"]: index,
            ["data-is-focusable"]: true,
            draggable: !disableReorder
        };
    }
    /**
     * @inheritdoc
     * @override
     */ renderListItem() {
        const { permissions, listItem: checklistItem, ariaLabel, theme, onDelete, onPromote } = this.props;
        const { editedTitle } = this.state;
        const disableEditTitle = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableEditTitle);
        const disableReorder = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableReorder);
        let componentClassNames = this.getClassNames(disableEditTitle);
        const itemTitle = /*#__PURE__*/ React.createElement("form", {
            onSubmit: this.onFormSubmit,
            autoComplete: "off"
        }, /*#__PURE__*/ React.createElement(TextField, {
            className: "checklistTextField",
            placeholder: checklistItem.title,
            onChange: (event, newValue)=>{
                this.checklistEditTitleChanged(newValue);
            },
            value: editedTitle,
            styleType: TextFieldType.Text,
            maxLength: ChecklistItemModel.TitleMaxLength,
            onKeyDown: this.onEditTitleKeyDown,
            disabled: disableEditTitle,
            componentRef: this.titleFieldRef,
            id: this.titleElementId,
            onBlur: this.onTitleFieldBlur
        }));
        const disableEditCheckbox = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableCheckedStateToggling);
        const disablePromote = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisablePromote);
        const disableDelete = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableDelete);
        const isReadOnly = disableDelete && disableEditCheckbox && disableEditTitle && disablePromote;
        if (isReadOnly !== disableEditTitle) {
            componentClassNames = this.getClassNames(isReadOnly);
        }
        const commandButtons = /*#__PURE__*/ React.createElement(React.Fragment, null, disablePromote ? null : /*#__PURE__*/ React.createElement(IconButton, {
            className: "checklistItem-promoteButton",
            iconProps: uploadIconProps,
            "aria-label": Strings.Checklist_PromoteItemCommandTitle,
            title: Strings.Checklist_PromoteItemCommandTitle,
            onClick: ()=>onPromote(checklistItem.id),
            theme: theme
        }), disableDelete ? null : /*#__PURE__*/ React.createElement(IconButton, {
            className: "checklistItem-deleteButton",
            iconProps: deleteIconProps,
            "aria-label": Strings.Checklist_RemoveItemCommandTitle,
            title: Strings.Checklist_RemoveItemCommandTitle,
            onClick: ()=>onDelete(checklistItem.id),
            theme: theme
        }));
        const componentJSX = /*#__PURE__*/ React.createElement("div", {
            draggable: !disableReorder,
            className: componentClassNames.listItemContents
        }, /*#__PURE__*/ React.createElement(Checkbox, {
            title: disableEditCheckbox ? Strings.SharedComponents_ReadonlyFieldPermissionsTooltip : Strings.Checklist_MarkCompleteCommandTitle,
            onChange: this.onCheckboxChange,
            checked: checklistItem.isChecked,
            inputProps: {
                tabIndex: -1,
                onFocus: this.onCheckboxFocus
            },
            ariaLabel: ariaLabel,
            disabled: disableEditCheckbox,
            appTheme: theme
        }), itemTitle, (!disablePromote || !disableDelete) && /*#__PURE__*/ React.createElement("div", {
            className: componentClassNames.checklistItemCommands
        }, commandButtons));
        return componentJSX;
    }
    /**
     * Helper function to set focus on this element sometime after render is complete
     * @param prevProps Previous properties
     * @param prevState Previous state
     * @override
     */ componentDidUpdate(prevProps, prevState) {
        setTimeout(()=>{
            if (this.props.setFocusOnInit && this.checklistItemElement.current) {
                this.checklistItemElement.current.focus();
                this.props.clearFocusedChecklistItemId();
            }
        }, 0);
    }
    /**
     * Get the classnames associated with the computed styles for the checklist item component
     */ getClassNames(isReadOnly) {
        return getClassNames(getStyles(this.props.theme, this.props.styles, isReadOnly, this.props.listItem.isChecked));
    }
    /**
     * Initializes a new instance of ChecklistItem
     * @param props Props for the list item control
     * @param context Context to pass onto base component
     */ constructor(props, context){
        super(props, context), this.checklistItemElement = /*#__PURE__*/ React.createRef(), this.titleFieldRef = /*#__PURE__*/ React.createRef(), this.isFormSubmitted = false, /**
     * When the check state of the checklist item's checkbox is changed, call the callback in the props
     * @param evt the UI event
     */ this.onCheckboxChange = (evt)=>{
            this.props.onCheckboxClick(this.props.listItem.id);
        }, /**
     * When the checkbox is focused, we want to set focus on the checklist item element
     */ this.onCheckboxFocus = ()=>{
            this.checklistItemElement.current?.focus();
        }, /**
     * Update a checklist item's title when checklist item is submitted by form
     * @param evt The form submit event
     */ this.onFormSubmit = (evt)=>{
            // We don't want the form to submit, so we prevent the default behavior
            evt.preventDefault();
            const { listItem, onRename } = this.props;
            const { editedTitle } = this.state;
            if (editedTitle !== "") {
                onRename(listItem.id, editedTitle);
            }
            if (evt.type === "submit") {
                // onFormSubmit is triggered onKeyDown with Enter button
                // The same Enter keypress will trigger this.onKeyUp(), which will attempt to put focus back into the Title textfield
                // So, we set this flag here to indicate we just submitted the form and don't want to re-focus on the Title textfield
                this.isFormSubmitted = true;
                // After submit, focus on the checklist item.
                this.checklistItemElement.current?.focus();
            }
        }, /**
     * When the title field loses focus, we want to commit the checklist item's title change
     */ this.onTitleFieldBlur = ()=>{
            const { listItem, onRename } = this.props;
            onRename(listItem.id, this.state.editedTitle);
        }, /**
     * Title for item being edited is updated
     * @param value Updated title for checklist item being edited
     */ this.checklistEditTitleChanged = (value)=>{
            this.setState({
                editedTitle: value
            });
        }, /**
     * Updates state to indicate we are editing the checklist item title text
     */ this.startChecklistItemTitleEdit = ()=>{
            this.setState({
                editedTitle: this.props.listItem.title,
                shouldEnterInnerFocusZone: true
            });
            setTimeout(()=>{
                // Always set focus in a setTimeout
                this.titleFieldRef.current?.focus();
            }, 0);
        }, /**
     * Callback when keydown event happens during checklist item name edit
     * This stop propagation for space events allows users to add spaces when editing
     * the checklist item title
     * @param evt The keyboard event
     */ this.onEditTitleKeyDown = (evt)=>{
            if (KeyboardUtilities.getKey(evt) === KeyCode.SPACE) {
                evt.stopPropagation();
            }
        }, /**
     * Process and handle keyboard commands on the checklist item
     * @param evt The ui event to process
     */ this.onKeyUp = (evt)=>{
            evt.preventDefault();
            const { permissions, listItem, onCheckboxClick, onDelete, onMove } = this.props;
            const element = evt.target;
            const keyEvt = evt;
            const keyPressed = KeyboardUtilities.getKey(keyEvt);
            if (element !== this.keyDownElement || element.className.indexOf("Upload") > -1 && !keyEvt.shiftKey) {
                this.keyDownElement = null;
                return;
            }
            this.keyDownElement = null;
            const disableDelete = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableDelete);
            const disableEditCheckbox = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableCheckedStateToggling);
            const disableEditTitle = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableEditTitle);
            const disableReorder = isChecklistPermissionDisabled(permissions, ChecklistPermissions.DisableReorder);
            const isOnChecklistItem = element.className === ChecklistItem.SUB_LI_DIV_CLASSNAME;
            // Edit mode
            // Only use Enter to trigger edit mode if we're focused on ListItem.
            // On MacOS using the Zhuyin traditional chinese keyboard, the enter for selecting a character causes
            // the changes to the checklist item to be lost without this check as the title is reset.
            if (keyPressed === KeyCode.ENTER && isOnChecklistItem && !disableEditTitle) {
                if (this.isFormSubmitted) {
                    // We just pressed Enter to submit the form for edit Title
                    // So we don't want to start editing again
                    this.isFormSubmitted = false;
                } else {
                    this.startChecklistItemTitleEdit();
                }
            } else if (keyPressed === KeyCode.DELETE && element.tagName !== "INPUT" && !disableDelete) {
                // Delete the selected item
                onDelete(listItem.id);
            } else if (keyPressed === KeyCode.SPACE && isOnChecklistItem && !disableEditCheckbox) {
                // toggle checked
                onCheckboxClick(listItem.id);
            } else if (keyPressed === KeyCode.UP_ARROW && keyEvt.ctrlKey && element.tagName !== "INPUT" && !disableReorder) {
                // Ctrl + Up + [Non-Edit mode] => Move item up
                onMove(listItem, -1);
            } else if (keyPressed === KeyCode.DOWN_ARROW && keyEvt.ctrlKey && element.tagName !== "INPUT" && !disableReorder) {
                // Ctrl + Down + [Non-Edit mode] => Move item down
                onMove(listItem, 1);
            } else if (isOnChecklistItem && (keyPressed === KeyCode.RIGHT_ARROW || keyPressed === KeyCode.DOWN_ARROW)) {
                evt.preventDefault();
                // Focus is on checklist item + NO editing.
                setTimeout(()=>{
                    // Always set focus in a setTimeout
                    this.titleFieldRef.current?.focus();
                }, 0);
                this.setState({
                    shouldEnterInnerFocusZone: true
                });
            }
        }, /**
     * Just prevent default for space bar presses. This keeps the page from scrolling down and from toggling check mark when pressing space on the buttons
     */ this.preventDefault = (evt)=>{
            this.keyDownElement = evt.currentTarget;
            const isOnChecklistItem = evt.currentTarget.className === ChecklistItem.SUB_LI_DIV_CLASSNAME;
            if (KeyboardUtilities.getKey(evt) === KeyCode.SPACE && isOnChecklistItem) {
                evt.preventDefault();
            } else if (evt.currentTarget.tagName === "INPUT" && (KeyboardUtilities.getKey(evt) === KeyCode.RIGHT_ARROW || KeyboardUtilities.getKey(evt) === KeyCode.DOWN_ARROW || KeyboardUtilities.getKey(evt) === KeyCode.UP_ARROW || KeyboardUtilities.getKey(evt) === KeyCode.LEFT_ARROW)) {
                evt.preventDefault();
            }
        };
        this.titleElementId = `checklistItemTitle-${this.props.listItem.id}`;
        this.state = {
            editedTitle: props.listItem.title,
            isListItemActive: false,
            shouldEnterInnerFocusZone: false
        };
    }
}
ChecklistItem.SUB_LI_DIV_CLASSNAME = "subLiDiv";
export default DragSource((props)=>props.itemTypeString, ChecklistItem.prototype.itemSource(), ChecklistItem.prototype.itemCollect)(ChecklistItem);
